import { useState, useEffect, useCallback } from "react";
import { Button, Col, Container, Image, Nav, Navbar } from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import LoginButton from "./LoginButton";
import GlobalSearchBox from "./components/GlobalSearchBox";
import { useStaffAuthorization } from "./admin/useStaffAuthorization";

const IconBox = ({ src, alt }: { src: string; alt: string }) => (
  <div className="sidebar-icon-box">
    <Image src={src} alt={alt} />
  </div>
);

interface NavBarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  canToggle?: boolean;
}

const NavBar = ({
  isCollapsed = false,
  onToggle,
  canToggle = true,
}: NavBarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  // Defense-in-depth: only show the /admin entry to staff. The server is the
  // source of truth (s-admin-authz-shell-advice-019e9cbb).
  const staffStatus = useStaffAuthorization();

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const handleToggle = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <Col
      xl={isCollapsed ? 1 : 2}
      lg={isCollapsed ? 1 : 3}
      className={`sidebar-col gx-3 gx-lg-0 px-0 ${isCollapsed ? "sidebar-collapsed" : ""}`}
    >
      {/* Backdrop overlay for mobile menu */}
      {isMenuOpen && (
        <div
          className="mobile-menu-backdrop d-lg-none"
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      <div className="sidebar">
        <Navbar
          expand="lg"
          variant="dark"
          className="py-lg-0"
          expanded={isMenuOpen}
          onToggle={handleToggle}
        >
          <Container fluid className="px-lg-0">
            <Navbar.Toggle aria-controls="responsive-navbar-nav" />
            <GlobalSearchBox className="d-lg-none" />
            <LoginButton variant="light" className="d-inline-block d-lg-none" />

            {canToggle && (
              <Button
                variant="outline-light"
                size="sm"
                className="d-none d-lg-block sidebar-toggle"
                onClick={onToggle}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  {isCollapsed ? (
                    // Expand - layout-sidebar-reverse
                    <path d="M16 3a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2zm-5-1v12H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm1 0h2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-2z" />
                  ) : (
                    // Collapse - layout-sidebar
                    <path d="M0 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5-1v12h9a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM4 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2z" />
                  )}
                </svg>
              </Button>
            )}

            <div
              id="responsive-navbar-nav"
              className={`navbar-collapse${isMenuOpen ? " show" : ""}`}
            >
              <Nav
                variant="pills"
                as="ul"
                defaultActiveKey="/dashboard"
                className="flex-column w-100"
              >
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/dashboard">
                    <IconBox src="/icons/home.svg" alt="home icon" />
                    <span className="align-middle">Home</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/clients">
                    <IconBox src="/icons/client.svg" alt="client icon" />
                    <span className="align-middle">Clients</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/models">
                    <IconBox src="/icons/models.svg" alt="models icon" />
                    <span className="align-middle">Models</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/rebalances">
                    <IconBox
                      src="/icons/trades.svg"
                      alt="trade proposals icon"
                    />
                    <span className="align-middle">Trade Proposals</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/cash">
                    <IconBox src="/icons/cash.svg" alt="cash icon" />
                    <span className="align-middle">Cash</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/billing">
                    <IconBox src="/icons/billing.svg" alt="billing icon" />
                    <span className="align-middle">Billing</span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link as={NavLink} to="/reports">
                    <IconBox src="/icons/manage.svg" alt="reporting icon" />
                    <span className="align-middle">Reporting</span>
                  </Nav.Link>
                </Nav.Item>
                {staffStatus === "staff" && (
                  <Nav.Item as="li">
                    <Nav.Link as={NavLink} to="/admin">
                      <IconBox src="/icons/manage.svg" alt="admin icon" />
                      <span className="align-middle">Admin</span>
                    </Nav.Link>
                  </Nav.Item>
                )}
              </Nav>
            </div>
          </Container>
        </Navbar>
      </div>
    </Col>
  );
};

export default NavBar;
